import { useEffect, useId, useRef, useState } from "react";
import { useT } from "../i18n/index.tsx";
import { formatDay, formatUsd, usdNumber } from "../lib/format.ts";

type Day = { date: string; deposits_usd: string; payouts_usd: string };

const DEP = "#0a5bd6"; // brand blue-deep
const PAY = "#12c9c0"; // brand teal

/** Round an axis max up to 1 / 2 / 2.5 / 5 × 10^n. */
function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * p) return m * p;
  return 10 * p;
}

/** Daily deposits vs payouts over the chosen range, grouped columns. Hand-rolled SVG with hover tooltips and a table view. */
export function FlowChart({ days, emptyText }: { days: Day[]; emptyText?: string }) {
  const t = useT();
  const c = t.overview.chart;
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  const titleId = useId();
  const plot = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = plot.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setW(Math.max(280, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [table]);
  const H = W < 500 ? 200 : 240;
  const pad = { l: 44, r: 4, t: 12, b: 28 };
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const max = niceMax(Math.max(...days.flatMap((d) => [usdNumber(d.deposits_usd), usdNumber(d.payouts_usd)]), 1));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const band = iw / Math.max(days.length, 1);
  const gap = Math.min(W < 500 ? 6 : 12, band * 0.3);
  const bw = Math.max(1, Math.min(14, (band - gap) / 2));
  const labelStep = Math.max(1, Math.ceil(52 / band));
  const y = (v: number) => pad.t + ih - (v / max) * ih;
  const bar = (x: number, v: number) => {
    const h = Math.max((v / max) * ih, v > 0 ? 2 : 0);
    const r = Math.min(4, h, bw / 2);
    const top = pad.t + ih - h;
    // Rounded data end (top), square at the baseline.
    return `M${x},${pad.t + ih} V${top + r} Q${x},${top} ${x + r},${top} H${x + bw - r} Q${x + bw},${top} ${x + bw},${top + r} V${pad.t + ih} Z`;
  };
  const hd = hover === null ? null : days[hover];
  const empty = days.every((d) => usdNumber(d.deposits_usd) === 0 && usdNumber(d.payouts_usd) === 0);

  return (
    <div className="chart">
      <div className="chart-head">
        <div>
          <h2 id={titleId} className="card-title">{c.title}</h2>
          <p className="card-sub">{c.sub}</p>
        </div>
        <div className="chart-tools">
          <ul className="legend" aria-label={c.legend}>
            <li><i style={{ background: DEP }} /> {c.deposits}</li>
            <li><i style={{ background: PAY }} /> {c.payouts}</li>
          </ul>
          <button type="button" className="link-btn small" onClick={() => setTable(!table)} aria-pressed={table}>
            {table ? c.showChart : c.showTable}
          </button>
        </div>
      </div>
      {table ? (
        <div className="table-wrap chart-table">
          <table>
            <caption className="sr-only">{c.tableCaption}</caption>
            <thead><tr><th scope="col">{c.day}</th><th scope="col" className="num">{c.deposits}</th><th scope="col" className="num">{c.payouts}</th></tr></thead>
            <tbody>
              {[...days].reverse().map((d) => (
                <tr key={d.date}><td>{formatDay(d.date)}</td><td className="num">{formatUsd(d.deposits_usd)}</td><td className="num">{formatUsd(d.payouts_usd)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="chart-plot" ref={plot} onMouseLeave={() => setHover(null)}>
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={titleId} className="chart-svg">
            {ticks.map((v) => (
              <g key={v}>
                <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} className="grid" />
                <text x={pad.l - 8} y={y(v)} className="axis" textAnchor="end" dominantBaseline="middle">{formatUsd(String(v), { compact: true }).replace(".00", "")}</text>
              </g>
            ))}
            {days.map((d, i) => {
              const x0 = pad.l + i * band + (band - (bw * 2 + 2)) / 2;
              return (
                <g key={d.date} className={hover !== null && hover !== i ? "dim" : undefined}>
                  <path d={bar(x0, usdNumber(d.deposits_usd))} fill={DEP} />
                  <path d={bar(x0 + bw + 2, usdNumber(d.payouts_usd))} fill={PAY} />
                  {(days.length - 1 - i) % labelStep === 0 && (
                    <text x={pad.l + i * band + band / 2} y={H - 8} className="axis" textAnchor="middle">{formatDay(d.date)}</text>
                  )}
                  <rect
                    x={pad.l + i * band} y={pad.t} width={band} height={ih} fill="transparent"
                    onMouseEnter={() => setHover(i)}
                    onClick={() => setHover(i)}
                  />
                </g>
              );
            })}
          </svg>
          {empty && emptyText && <p className="chart-empty" style={{ top: pad.t, height: ih, left: pad.l }}>{emptyText}</p>}
          {hd && hover !== null && (
            <div className="chart-tip" style={{ left: Math.min(Math.max(pad.l + hover * band + band / 2, 90), W - 90) }} role="presentation">
              <strong>{formatDay(hd.date)}</strong>
              <span><i style={{ background: DEP }} /> {c.deposits} <b>{formatUsd(hd.deposits_usd)}</b></span>
              <span><i style={{ background: PAY }} /> {c.payouts} <b>{formatUsd(hd.payouts_usd)}</b></span>
            </div>
          )}
        </div>
      )}
      {table && empty && emptyText && <p className="chart-empty-note">{emptyText}</p>}
    </div>
  );
}
